// @ts-strict-ignore
import type { ToolConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/tools/tool-config'
import type { RegularFilterValueConfig } from '@empathybroker/tsclient-play/dist/models/instance/filters/regular-filter-value-config'
import type { Route } from 'vue-router'
import type {
  IToolingListComposableViewData,
  SetEnablePayload,
} from '../composables/use-tooling-list'
import type { CrudDataTypeEnum } from '../enums'
import type {
  DataTableColumn,
  DataTableOption,
  GuardResolveFunction,
  Pagination,
  RegularFilterConfig,
  Sort,
} from '../models'
import { mapActions, mapGetters } from 'vuex'
import { useFilters } from '../composables/use-filters'
import { useToolingPagination } from '../composables/use-tooling-pagination'
import { useToolingSorting } from '../composables/use-tooling-sorting'
import { FilterIdEnum, FilterTypeEnum, SortDirectionEnum } from '../enums'
import { capitalizeSentence } from '../filters/capitalize-sentence.filter'
import { CrudFormMixin } from '../mixins/crud-form.mixin'
import { ToolItemAction } from '../models'
import { ROUTER_CONSTS } from '../router/router.consts'
import { APP_CONSTS } from '../store/app/app.consts'
import { CONFIG_CONSTS } from '../store/config/config.consts'
import { CONTEXT_CONSTS } from '../store/context/context.consts'
import { CRUD_CONSTS } from '../store/crud/crud.consts'

export interface CrudListData extends IToolingListComposableViewData {
  dataType?: CrudDataTypeEnum
  columns: DataTableColumn[]
  strictMatching: boolean
  selection: unknown[]
  contextWatchDisabled: boolean
  ignoreQuery: boolean
  optionSelected: unknown
  loadingData: boolean
}

export const CrudListMixin = {
  computed: {
    ...mapGetters(CONFIG_CONSTS.moduleName, {
      instanceConfig: CONFIG_CONSTS.getters.currentInstance,
    }),
    ...mapGetters(CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context,
    }),
    ...mapGetters(CRUD_CONSTS.moduleName, {
      state: CRUD_CONSTS.getters.state,
    }),
    ...mapGetters(APP_CONSTS.moduleName, {
      featuresTree: APP_CONSTS.getters.featuresTree,
    }),
    data() {
      Object.assign(
        this.state.data,
        this.state.data.forEach(
          dataItem =>
            (dataItem.options = [
              {
                id: ToolItemAction.Edit,
                text: this.$epb.messages.crud.actions.update,
              },
              {
                id: ToolItemAction.Copy,
                text: this.$epb.messages.crud.actions.clone,
              },
              {
                id: ToolItemAction.Delete,
                text: this.$epb.messages.crud.actions.delete,
              },
              {
                id: ToolItemAction.Enable,
                text: this.$epb.messages.crud.actions.enable,
                disabled: dataItem.enabled,
              },
              {
                id: ToolItemAction.Disable,
                text: this.$epb.messages.crud.actions.disable,
                disabled: !dataItem.enabled,
              },
            ]),
        ),
      )
      return this.state.dataType === this.dataType ? this.state.data : []
    },
    toolConfig(): ToolConfig {
      return CrudFormMixin.computed.toolConfig.bind(this)()
    },
    readOnly(): boolean {
      return CrudFormMixin.computed.readOnly.bind(this)()
    },
    visibleFiltersConfig(): Array<RegularFilterConfig> {
      const { availableFilters } = useFilters()
      return Object.values(availableFilters.value).reduce<Array<RegularFilterConfig>>(
        (result, filter) => {
          if (
            filter.config.type === FilterTypeEnum.REGULAR &&
            filter.config.id !== FilterIdEnum.ACTIVE
          ) {
            const filterConfig = filter.config as RegularFilterConfig
            if (filterConfig.values.length > 1) {
              result.push(filterConfig)
            }
          }
          return result
        },
        [],
      )
    },
    visiblePivotsConfig(): Array<RegularFilterConfig> {
      return this.visibleFiltersConfig.filter(filterConfig =>
        this.toolConfig.pivotIds.includes(filterConfig.id),
      )
    },
    filtersColumns(): Array<DataTableColumn> {
      return this.getFilterColumnsFromConfigs(this.visibleFiltersConfig)
    },
    pivotsColumns(): Array<DataTableColumn> {
      return this.getFilterColumnsFromConfigs(this.visiblePivotsConfig)
    },
    requestFilters() {},
  },
  setup() {
    const { pagination, itemsPerPageOptions, setToolingPagination } = useToolingPagination()

    const { sort, setToolingSorting } = useToolingSorting({
      defaultSortValue: { direction: SortDirectionEnum.DESCENDING, field: 'meta.updateDate' },
    })

    return { pagination, itemsPerPageOptions, sort, setToolingPagination, setToolingSorting }
  },
  data(): CrudListData {
    return {
      dataType: undefined,
      routes: {
        create: undefined,
        update: undefined,
      },
      columns: [],
      messages: undefined,
      strictMatching: true,
      selection: [],
      contextWatchDisabled: false,
      ignoreQuery: false,
      optionSelected: undefined,
      loadingData: false,
    }
  },
  methods: {
    ...mapActions(CONTEXT_CONSTS.moduleName, {
      setQuery: CONTEXT_CONSTS.actions.setQuery,
      setContextStrictMatching: CONTEXT_CONSTS.actions.setStrictMatching,
    }),
    ...mapActions(CRUD_CONSTS.moduleName, {
      list: CRUD_CONSTS.actions.list,
      delete: CRUD_CONSTS.actions.delete,
      update: CRUD_CONSTS.actions.update,
    }),
    async loadData() {
      this.loadingData = true
      try {
        await this.list({
          dataType: this.dataType,
          sort: this.sort,
          pagination: this.pagination,
          requestFilters: this.requestFilters,
          ...(!this.ignoreQuery && { query: this.context.query }),
          strictMatching: this.strictMatching,
        })
        this.pagination.totalItems = this.state.pagination.totalItems
      } catch (error) {
        this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
      } finally {
        this.loadingData = false
      }
    },
    getPivotColumnId(pivotConfig: RegularFilterConfig): string {
      return pivotConfig.toolingId ?? pivotConfig.id
    },
    getPivotText(pivotConfig: RegularFilterConfig, dataItem): string {
      return this.getFilterText(
        pivotConfig.id,
        dataItem.context.pivots[this.getPivotColumnId(pivotConfig)],
      )
    },
    getFilterText(filterId: string, filterValues: string | string[]): string {
      if (!Array.isArray(filterValues)) {
        filterValues = filterValues ? [filterValues] : []
      }
      if (!filterValues.length) {
        return this.$epb.messages.filters.values.all.value
      }
      return filterValues
        .map((filterValue: string) => {
          const filterValueConfig = this.context.filters[filterId].config.values.find(
            (filterValueCfg: RegularFilterValueConfig) =>
              filterValueCfg.taggingId === filterValue || filterValueCfg.id === filterValue,
          )
          if (filterValueConfig) {
            const filterValuesMessages = this.$epb.messages.filters.values[filterId]
            return (
              (!!filterValueConfig && filterValueConfig.prettyName) ||
              (!!filterValuesMessages && filterValuesMessages[filterValueConfig.id]) ||
              filterValueConfig.id
            )
          }
          return filterValue
        })
        .join(', ')
    },
    async onQueryChange(query: string): Promise<void> {
      await this.setQuery(query || undefined)
      this.$emit('contextChanged')
    },
    onStrictMatchingChange(): void {
      this.strictMatching = !this.strictMatching
      this.contextWatchDisabled = !this.context?.query?.length

      this.setContextStrictMatching(this.strictMatching)
      this.$emit('contextChanged')
    },
    onSortChange(sort: Sort) {
      this.setToolingSorting(sort)
      this.pagination.page = 1
      this.loadData()
    },
    onPaginationChange(pagination: Pagination) {
      this.setToolingPagination(pagination)
      this.loadData()
    },
    onSelectionChange(selection: any[]) {
      this.selection = selection
      this.optionSelected = undefined
    },
    async onOptionClick(option: DataTableOption, dataItem, crudListRef): Promise<void> {
      this.optionSelected = dataItem
      if (option.id === ToolItemAction.Edit) {
        this.navigateToRoute(this.routes.update, dataItem)
      } else if (option.id === ToolItemAction.Copy) {
        this.navigateToRoute(this.routes.create, dataItem)
      } else if (option.id === ToolItemAction.Delete) {
        crudListRef.$refs.deleteDialog.open()
      } else if (option.id === ToolItemAction.Enable) {
        crudListRef.onEnable()
      } else if (option.id === ToolItemAction.Disable) {
        await this.setEnabled({ enabled: false })
      }
    },
    onCheckboxClick(dataItem): void {
      if (this.readOnly) {
        this.navigateToRoute(this.routes.update, dataItem)
      }
    },
    onRowClick(dataItem): void {
      this.navigateToRoute(this.routes.update, dataItem)
    },
    navigateToRoute(routeName: string, dataItem?) {
      let id
      if (dataItem) {
        id = dataItem.id
      } else {
        id = this.selection.map(selectedItem => selectedItem.id).join() || undefined
      }
      this.$router.push({
        ...this.$route,
        name: routeName,
        params: {
          ...this.$route.params,
          [ROUTER_CONSTS.params.id]: id,
        },
      })
    },
    async onDelete() {
      this.loadingData = true
      try {
        const selectedItems = this.optionSelected ? [this.optionSelected] : this.selection
        await this.delete({
          dataType: this.dataType,
          ids: this.optionSelected
            ? [this.optionSelected.id]
            : this.selection.map((selectedItem: any) => selectedItem.id),
        })
        this.$emit(
          'showSnackbar',
          selectedItems.length > 1
            ? this.messages.deleteMany.success
            : this.messages.deleteOne.success,
        )
        if (!this.data.length && this.pagination.page > 1) {
          this.pagination.page -= 1
        }
      } catch (error) {
        this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
      } finally {
        this.loadingData = false
        this.loadData()
      }
    },
    async enabledUpdate(enabled: boolean) {
      const selectedValues = this.optionSelected ? [this.optionSelected] : this.selection
      await this.update({
        dataType: this.dataType,
        items: selectedValues.map((selectedItem: any) => ({
          ...selectedItem,
          enabled,
        })),
      })
      this.$emit(
        'showSnackbar',
        enabled ? this.messages.enable.success : this.messages.disable.success,
      )
    },
    async setEnabled({ enabled }: SetEnablePayload<any>) {
      this.loadingData = true
      try {
        await this.enabledUpdate(enabled)
      } catch (error) {
        this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
      } finally {
        this.loadingData = false
        this.loadData()
      }
    },
    getFilterColumnsFromConfigs(configs: Array<RegularFilterConfig>): Array<DataTableColumn> {
      return configs.map(config => {
        const text =
          config.prettyName ??
          this.$epb.messages.filters.names[config.id] ??
          capitalizeSentence(config.id)
        return {
          id: this.getPivotColumnId(config),
          text,
          unwrapText: true,
          sortable: true,
        }
      })
    },
  },
  async created(): Promise<void> {
    if (typeof this.context.strictMatching === 'boolean') {
      this.strictMatching = this.context.strictMatching
    }

    await this.loadData()
  },
  async beforeRouteLeave(to: Route, from: Route, next: GuardResolveFunction) {
    this.contextWatchDisabled = true

    next()
  },
  watch: {
    context: {
      handler() {
        if (!this.contextWatchDisabled) {
          this.pagination.page = 1
          this.loadData()
        }

        this.contextWatchDisabled = false
      },
      deep: true,
    },
    requestFilters: {
      handler() {
        this.loadData()
      },
      deep: true,
    },
  },
}
